import { useMemo, useCallback } from 'react';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { useRouteMatch } from 'react-router-dom';
import useUser from './useUser';
import useCustomer from './useCustomer';

const useDataSource = (props) => {
  const routeMatch = useRouteMatch('/data-sources/:dataSourceId');
  const { extraFields, websiteFields, activeLicenseFields, includeLicenseUsage = false, skip = false, fetchPolicy = 'cache-first' } = props || {};
  const { loading: userLoading, hasDataSourceAccess, hasDataSourcePermission } = useUser();
  const { customerAccess, loading: customerLoading } = useCustomer({ skip });

  const dataSourceId = props?.dataSourceId || routeMatch?.params?.dataSourceId;

  const query = gql`
    query UseDataSource($id: ID!) {
      dataSource(id: $id) {
        id
        title
        type
        customer {
          id
          disableAI
        }
        website { 
          id
          ${websiteFields || ''}
        }
        cx { 
          id 
        }
        activeLicense {
          id
          title
          startDate
          endDate
          renewalDate
          interval
          settings
          license {
            id
            title
            uniqueTitle
            level
          }
          modules {
            id
            module 
            title
            uniqueTitle
            level
          }           
          ${activeLicenseFields || ''}
        }
        ${includeLicenseUsage ? `currentUsage {
            id
            period
            startDate
            endDate
            setting
            limit
            current
            estimated    
          }` : ''}         
        ${extraFields || ''}
      }
    }
  `;

  const { data: { dataSource } = {}, loading: dataSourceLoading, updating, fetching, refetch } = useQuery(query, {
    variables: { id: dataSourceId },
    skip: skip || !dataSourceId,
    fetchPolicy: extraFields || websiteFields || activeLicenseFields || includeLicenseUsage ? 'cache-and-network' : fetchPolicy,
  });

  const loading = userLoading || customerLoading || dataSourceLoading;

  const dataSourceAccess = useMemo(() => customerAccess?.dataSourceAccess.find((da) => da.dataSource.id === dataSourceId), [customerAccess, dataSourceId]);

  const hasAccess = useCallback(() => hasDataSourceAccess(dataSourceId), [hasDataSourceAccess, dataSourceId]);
  const hasPermission = useCallback((permission) => hasDataSourcePermission(dataSourceId, permission), [hasDataSourcePermission, dataSourceId]);

  const getDataSourceLicenseSetting = useCallback((setting) => dataSource?.activeLicense?.settings?.[setting], [dataSource]);
  const getDataSourceUsage = useCallback((setting) => dataSource?.currentUsage?.find(u => u.setting === setting), [dataSource]);

  return {
    dataSource,
    dataSourceId,
    aiDisabled: dataSource?.customer?.disableAI === true,
    dataSourceAccess,
    hasDataSourceAccess: hasAccess,
    hasDataSourcePermission: hasPermission,
    getDataSourceLicenseSetting,
    getDataSourceUsage,
    loading,
    updating,
    fetching,
    refetch,
  };
};

export default useDataSource;
